import { inject, Injectable, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import {
  AUTH,
  AUTH_LOGIN,
  AUTH_RESET_PASSWORD,
  AUTH_VERIFY_OTP,
  ErrorMessages
} from '@smarsh-cyber/shared/constants';
import {
  DEFAULT_BASE_RES,
  GlobalState,
  REQ,
  ReqStatus
} from '@smarsh-cyber/shared/models';
import { ToastrService } from 'ngx-toastr';
import { exhaustMap, map, Observable } from 'rxjs';
import {
  AppFacadeService,
  selectIsError,
  selectIsLoaded,
  selectIsLoading
} from '@smarsh-cyber/app-state';
import {
  ResetPasswordREQ,
  VerifyOtpREQ,
  VerifyUserREQ
} from '../models/api_models';
import {
  LoginModel,
  ResetPasswordModel,
  VerifyOtpModel,
  VerifyUserModel
} from '../models/ui_models';
import { AuthUiService } from '../services/auth.ui.service';
import { AuthState, initialAuthState } from './auth.state';
import {
  GetLoginOTPModel,
  VerifyLoginOTPModel
} from '../models/ui_models/passcode/otp-login';
import { GetLoginOtpREQ } from '../models/api_models/passcode/get-login-otp.res';
import { VerifyLoginOtpREQ } from '../models/api_models/passcode/verify-login-otp.res';

@Injectable()
export class AuthComponentStore extends ComponentStore<AuthState> {
  private toasterService = inject(ToastrService);
  private store = inject(Store<GlobalState>);
  authService = inject(AuthUiService);
  appFacade = inject(AppFacadeService);

  constructor(private router: Router) {
    super(initialAuthState);
  }

  readonly isLoading$ = this.store.selectSignal(selectIsLoading);
  readonly isLoaded$ = this.store.selectSignal(selectIsLoaded);
  readonly isError$ = this.store.selectSignal(selectIsError);

  readonly login$: Signal<LoginModel> = this.selectSignal(
    (state) => state.login
  );

  readonly verifyUser$: Signal<VerifyUserModel> = this.selectSignal(
    (state) => state.verifyUser
  );

  readonly verifyOtp$: Signal<VerifyOtpModel> = this.selectSignal(
    (state) => state.verifyOtp
  );

  readonly resetPassword$: Signal<ResetPasswordModel> = this.selectSignal(
    (state) => state.resetPassword
  );

  readonly getLoginOtp$: Signal<GetLoginOTPModel> = this.selectSignal(
    (state) => state.getLoginOtp
  );

  readonly verifyLoginOtp$: Signal<VerifyLoginOTPModel> = this.selectSignal(
    (state) => state.verifyLoginOtp
  );

  private readonly loginUpdater = this.updater((state, login: LoginModel) => ({
    ...state,
    login
  }));

  private readonly verifyUserUpdater = this.updater(
    (state, verifyUser: VerifyUserModel) => ({
      ...state,
      verifyUser
    })
  );

  private readonly verifyOtpUpdater = this.updater(
    (state, verifyOtp: VerifyOtpModel) => ({
      ...state,
      verifyOtp
    })
  );

  private readonly resetPassUpdater = this.updater(
    (state, resetPass: ResetPasswordModel) => ({
      ...state,
      resetPass
    })
  );

  private readonly getLoginOtpUpdater = this.updater(
    (state, getLoginOtp: GetLoginOTPModel) => ({
      ...state,
      getLoginOtp
    })
  );

  private readonly verifyLoginOtpUpdater = this.updater(
    (state, verifyLoginOtp: VerifyLoginOTPModel) => ({
      ...state,
      verifyLoginOtp
    })
  );

  // Login Effect
  login = this.effect(
    (
      req$: Observable<{
        username: string;
        password: string;
        portalName: string;
        portalUrl: string;
        isWebAuthLoginAttempt: boolean;
      }>
    ) => {
      return req$.pipe(
        exhaustMap(
          ({
            username,
            password,
            portalName,
            portalUrl,
            isWebAuthLoginAttempt
          }) => {
            this.patchState((state) => ({
              ...state,
              login: {
                ...state.login,
                reqState: {
                  ...state.login.reqState,
                  reqStatus: ReqStatus.LOADING
                }
              }
            }));
            return this.authService.login({ username, password }).pipe(
              map((res: LoginModel) => {
                this.loginUpdater(res);
                // if (REQ.isError(res?.reqState) && isWebAuthLoginAttempt)
                //   this.toasterService.error(
                //     'You have used all available temporary access requests and access is not available at this time'
                //   );
                // else
                // if (REQ.isError(res?.reqState) && !isWebAuthLoginAttempt) {
                //   this.toasterService.error(res?.reqState?.message);
                // }
                if (REQ.isError(res?.reqState) && !isWebAuthLoginAttempt) {
                  this.toasterService.error(res?.reqState?.message);
                } else {
                  if (res.data && !isWebAuthLoginAttempt)
                    this.appFacade.initNgpLogin(
                      portalName,
                      portalUrl,
                      res.data
                    );
                  else {
                    if (isWebAuthLoginAttempt) {
                      //Do nothing;
                    } else this.toasterService.error(res?.reqState?.message);
                  }
                }
              })
            );
          }
        )
      );
    }
  );

  // Reset Password Effect
  resetPassword = this.effect((req$: Observable<ResetPasswordREQ>) => {
    return req$.pipe(
      exhaustMap((resetReq) => {
        this.patchState((state) => ({
          ...state,
          resetPassword: {
            ...state.resetPassword,
            reqState: {
              ...state.resetPassword.reqState,
              reqStatus: ReqStatus.LOADING
            }
          }
        }));
        return this.authService.resetPassword(resetReq).pipe(
          map((res: ResetPasswordModel) => {
            this.resetPassUpdater(res);
            if (REQ.isError(res?.reqState)) {
              this.toasterService.error(res?.reqState?.message);
            } else {
              this.toasterService.success(ErrorMessages.RESET_PASS_SUCCESS);
              this.router.navigate([AUTH, AUTH_LOGIN], {
                queryParams: {
                  portalName: this.appFacade.$userToken().portalName
                },
                queryParamsHandling: 'preserve'
              });
            }
          })
        );
      })
    );
  });

  // Verify OTP Effect
  verifyOtp = this.effect((req$: Observable<VerifyOtpREQ>) => {
    return req$.pipe(
      exhaustMap((req) => {
        this.patchState((state) => ({
          ...state,
          verifyOtp: {
            ...state.verifyOtp,
            reqState: {
              ...state.verifyOtp.reqState,
              reqStatus: ReqStatus.LOADING
            }
          }
        }));
        return this.authService.verifyOtp(req).pipe(
          map((res: VerifyOtpModel) => {
            this.verifyOtpUpdater(res);
            if (REQ.isError(res?.reqState)) {
              this.toasterService.error(res?.reqState?.message);
            } else {
              this.toasterService.success(ErrorMessages.OTP_VERIFY_SUCCESS);
              this.router.navigate([AUTH, AUTH_RESET_PASSWORD], {
                queryParams: {
                  portalName: this.appFacade.$userToken().portalName
                },
                state: {
                  user: this.appFacade.$userDetails(),
                  otpToken: res.data.userToken
                },
                queryParamsHandling: 'preserve'
              });
            }
          })
        );
      })
    );
  });

  // Verify User Effect
  verifyUser = this.effect((req$: Observable<VerifyUserREQ>) => {
    return req$.pipe(
      exhaustMap((req) => {
        this.patchState((state) => ({
          ...state,
          verifyUser: {
            ...state.verifyUser,
            reqState: {
              ...state.verifyUser.reqState,
              reqStatus: ReqStatus.LOADING
            }
          }
        }));
        return this.authService.verifyUser(req).pipe(
          map((res: VerifyUserModel) => {
            this.verifyUserUpdater(res);
            if (REQ.isError(res?.reqState)) {
              this.toasterService.error(res?.reqState?.message);
            } else {
              this.toasterService.success(ErrorMessages.OTP_SENT);
              this.router.navigate([AUTH, AUTH_VERIFY_OTP], {
                queryParams: {
                  portalName: this.appFacade.$userToken().portalName
                },
                state: {
                  portalName: this.appFacade.$userToken().portalName,
                  username: req.username
                },
                queryParamsHandling: 'preserve'
              });
            }
          })
        );
      })
    );
  });

  resetLoginState() {
    this.loginUpdater(DEFAULT_BASE_RES);
  }

  resetGetLoginOtpState() {
    this.getLoginOtpUpdater(DEFAULT_BASE_RES);
  }

  resetVerifyLoginOtpState() {
    this.verifyLoginOtpUpdater(DEFAULT_BASE_RES);
  }

  // Get Login OTP Effect
  getLoginOtp = this.effect((req$: Observable<GetLoginOtpREQ>) => {
    return req$.pipe(
      exhaustMap((req) => {
        this.patchState((state) => ({
          ...state,
          getLoginOtp: {
            ...state.getLoginOtp,
            reqState: {
              ...state.getLoginOtp.reqState,
              reqStatus: ReqStatus.LOADING
            }
          }
        }));
        return this.authService.getLoginOtp(req).pipe(
          map((res: GetLoginOTPModel) => {
            this.getLoginOtpUpdater(res);
            if (REQ.isError(res?.reqState)) {
              this.toasterService.error(res?.reqState?.message);
            } else {
              this.toasterService.success(ErrorMessages.OTP_SENT);
            }
          })
        );
      })
    );
  });

  // Verify Login OTP Effect
  // currently being used in webauth flow only
  verifyLoginOtp = this.effect(
    (
      req$: Observable<
        [
          req: VerifyLoginOtpREQ
          // portalName: string,
          // portalUrl: string,
          // isWebAuthLoginAttempt: boolean
        ]
      >
    ) => {
      return req$.pipe(
        exhaustMap(([req]) => {
          this.patchState((state) => ({
            ...state,
            verifyLoginOtp: {
              ...state.verifyLoginOtp,
              reqState: {
                ...state.getLoginOtp.reqState,
                reqStatus: ReqStatus.LOADING
              }
            }
          }));
          return this.authService.verifyLoginOtp(req).pipe(
            map((res: VerifyLoginOTPModel) => {
              this.verifyLoginOtpUpdater(res);
            })
          );
        })
      );
    }
  );
}
